import React, { useState } from 'react';

import { Timestamp } from '@shared/components/helpers';

import { Panel } from '@admin/components/helpers/panel';
import { usePermission } from '@admin/hooks/use_permission';
import { WAREHOUSE_OPS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useWarehouseTaskCommentCreateMutation } from '@admin/schema';

interface Comment {
  id: string;
  body: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
  };
}

interface CommentsProps {
  taskId: string;
  comments: Comment[];
  onCommentAdded: () => void;
}

export const Comments: React.FC<CommentsProps> = ({ taskId, comments, onCommentAdded }) => {
  const canUpdateTask = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.update);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const [createComment] = useWarehouseTaskCommentCreateMutation({ client });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;

    setSubmitting(true);
    setCommentError(null);
    try {
      const result = await createComment({
        variables: { warehouseTaskID: taskId, body: body.trim() },
      });
      if (result.data?.warehouseTaskCommentCreate.status !== 'OK') {
        setCommentError(result.data?.warehouseTaskCommentCreate.error || 'Failed to post comment');
        return;
      }
      setBody('');
      onCommentAdded();
    } catch {
      setCommentError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Comments ({comments.length})</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {comments.length === 0 ? (
          <p className="text-muted">No comments yet</p>
        ) : (
          <div>
            {comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  borderBottom: '1px solid #eee',
                  paddingBottom: 12,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>{comment.user.name}</strong>
                  <Timestamp value={comment.createdAt} format={Timestamp.Format.DateTime} />
                </div>
                <p style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>{comment.body}</p>
              </div>
            ))}
          </div>
        )}

        {canUpdateTask && (
          <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
            <div className="form-group">
              <textarea
                className="form-control"
                rows={3}
                placeholder="Add a comment..."
                aria-label="Comment body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            {commentError && (
              <div className="alert alert-danger" style={{ marginBottom: 8 }}>
                {commentError}{' '}
                <button type="submit" className="btn btn-link btn-xs" style={{ padding: 0 }}>
                  Retry
                </button>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting || !body.trim()}>
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
        )}
      </Panel.Body>
    </Panel>
  );
};
